import {
  Alert,
  DialogContent,
  DialogTitle,
  IconButton,
  useTheme,
} from "@mui/material";
import {
  SHButton,
  SHCheckbox,
  SHDialog,
  SHFormControlLabel,
  SHLabel,
  SHStack,
  SHTextField,
  SHTypography,
} from "@components/design-systems";
import { CloseButton } from "@components/buttons/close";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { ModelScreenerExportModelsDto } from "@models/model-screener/entities/modelScreenerData";
import { useSelector } from "react-redux";
import { RootState } from "@redux/store";
import { useEffect, useMemo } from "react";
import { DownloadSVG, InfoSVG } from "@components/svgs";
import {
  MSExcelDownloadConstants,
  MSWordDownloadConstants,
} from "@pages/managed-accounts/model-screener/constant";
import { BetaBadge } from "@components/beta-badge";
import { useUserPermissions } from "@hooks/userUserPermission";
import { isEmpty } from "lodash";
import { useModelScreener } from "@hooks/useModelScreener";
import { hexToRGBA } from "@utils";
import { downloadMSExcels } from "@pages/managed-accounts/model-screener/components/model-screener-export/utils";

type MSExportDialogProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const MSExportDialog = ({ isOpen, onClose }: MSExportDialogProps) => {
  const { palette } = useTheme();

  const { exportModelScreenerExcel } = useModelScreener();

  const { isSupplierBdmSale } = useUserPermissions();
  const modelScreenerData = useSelector(
    (state: RootState) => state.modelScreener.originalData,
  );
  const selectedModelRecord = useSelector(
    (state: RootState) => state.modelScreener.selectedRowIds,
  );
  const isExportingExcel = useSelector(
    (state: RootState) => state.modelScreener.ui.isExportingExcel,
  );
  const [performanceStartDate, performanceEndDate] = useSelector(
    (state: RootState) => state.modelScreener.currentSelectedDateRange,
  );

  const formMethod = useForm<ModelScreenerExportModelsDto>({
    mode: "onChange",
    defaultValues: { ...new ModelScreenerExportModelsDto() },
  });

  const [
    adviserName,
    practiceName,
    managerDownload,
    seriesDownload,
    modelDownload,
  ] = useWatch({
    control: formMethod.control,
    name: [
      "adviserName",
      "practiceName",
      "managerDownload",
      "seriesDownload",
      "modelDownload",
    ],
  });

  useEffect(() => {
    formMethod.reset();
    // eslint-disable-next-line
  }, [isOpen]);

  const selectedModelIds = useMemo(
    () => Object.keys(selectedModelRecord),
    [selectedModelRecord],
  );

  const isRequiredAdviserName = useMemo(() => {
    return !isEmpty(practiceName) && isEmpty(adviserName);
  }, [practiceName, adviserName]);

  const isRequiredPracticeName = useMemo(() => {
    return isEmpty(practiceName) && !isEmpty(adviserName);
  }, [practiceName, adviserName]);

  const isExcelButtonDisabled = useMemo(() => {
    // Check if any download options are selected
    const hasSelectedDownloadOptions = [
      managerDownload,
      seriesDownload,
      modelDownload,
    ].some(Boolean);

    // Validate selection count
    const selectedCount = selectedModelIds.length;
    const hasValidSelectionCount =
      selectedCount > 0 &&
      selectedCount <= MSExcelDownloadConstants.MaxSelected;

    // Validate owner information when required
    const hasValidOwnerInfo =
      !isSupplierBdmSale || (!isRequiredAdviserName && !isRequiredPracticeName);

    return (
      !hasSelectedDownloadOptions ||
      !hasValidSelectionCount ||
      isExportingExcel ||
      !hasValidOwnerInfo
    );
  }, [
    selectedModelIds,
    isSupplierBdmSale,
    managerDownload,
    seriesDownload,
    modelDownload,
    isExportingExcel,
    isRequiredPracticeName,
    isRequiredAdviserName,
  ]);

  const handleOnExcelDownload = async () => {
    if (isExcelButtonDisabled) return;

    const selectedRows = modelScreenerData?.data?.filter(
      (row) => !!selectedModelRecord[row.id],
    );
    const headers = modelScreenerData?.headers;
    formMethod.setValue("rows", selectedRows);
    formMethod.setValue("headers", headers);
    formMethod.setValue("exportExcel", true);
    formMethod.setValue("exportWord", false);
    formMethod.setValue("performanceStartDate", performanceStartDate);
    formMethod.setValue("performanceEndDate", performanceEndDate);
    const formData = formMethod.getValues();

    const response = await exportModelScreenerExcel(formData);

    if (!response) return;

    await downloadMSExcels({
      excelDataDto: response,
    });
  };

  return (
    <SHDialog
      open={isOpen}
      disableBackdropClick
      sx={{
        "& .MuiPaper-root": {
          maxWidth: "820px",
          maxHeight: "685px",
          padding: "40px 24px",
          position: "relative",
        },
      }}
    >
      <DialogTitle padding={"0 !important"}>
        <SHStack direction={"row"}>
          <SHTypography variant="h2" fontWeight={600}>
            Download settings
          </SHTypography>

          <SHStack
            sx={{
              position: "absolute",
              top: "22px",
              right: "25px",
            }}
            onClick={onClose}
          >
            <CloseButton width="12px" height="12px" />
          </SHStack>
        </SHStack>
      </DialogTitle>

      <DialogContent
        sx={{
          marginTop: { md: "10px", lg: 2 },
          padding: 0,
        }}
      >
        <FormProvider {...formMethod}>
          <SHStack gap={2}>
            {isSupplierBdmSale && (
              <SHStack
                sx={{
                  width: "100%",
                }}
                direction="row"
                gap={7}
              >
                <SHStack width={"50%"} textAlign={"left"}>
                  <SHTypography variant={"subtitle2"}>
                    Who is this download for?
                  </SHTypography>
                  <SHTypography variant={"body2"}>
                    If using the report externally, please specify for which
                    adviser and advice practice you are running this analysis
                  </SHTypography>
                </SHStack>

                <SHStack width={"50%"} gap={3}>
                  <Controller
                    name={"adviserName"}
                    render={({ field: { ref, ...others } }) => {
                      return (
                        <SHTextField
                          {...others}
                          label={"Adviser name"}
                          dotGroupProps={{
                            redDot: isRequiredAdviserName,
                          }}
                          placeholder={"Enter adviser name"}
                          helperText={
                            isRequiredAdviserName &&
                            "Adviser name is required when a practice name is provided"
                          }
                        />
                      );
                    }}
                  />

                  <Controller
                    name={"practiceName"}
                    render={({ field: { ref, ...others } }) => {
                      return (
                        <SHTextField
                          {...others}
                          label={"Practice name"}
                          dotGroupProps={{
                            redDot: isRequiredPracticeName,
                          }}
                          placeholder={"Enter practice name"}
                          helperText={
                            isRequiredPracticeName &&
                            "Practice name is required when an adviser name is provided"
                          }
                        />
                      );
                    }}
                  />
                </SHStack>
              </SHStack>
            )}

            <SHStack
              sx={{
                width: "100%",
                marginTop: isSupplierBdmSale ? "20px" : 0,
              }}
              direction="row"
              gap={7}
            >
              <SHStack width={"50%"} textAlign={"left"}>
                <SHTypography variant="body2" fontWeight={600}>
                  Download settings
                </SHTypography>
                <SHTypography variant={"body2"}>
                  Select which details you would like to download for the
                  selected models. For MS Excel downloads, each section will be
                  in a separate file. For MS Word, each will be in the same
                  document
                </SHTypography>
              </SHStack>

              <SHStack width={"50%"} gap={1} textAlign={"left"}>
                <SHLabel
                  label={
                    <SHTypography variant={"body2"} fontWeight={600}>
                      What should your download include?
                    </SHTypography>
                  }
                  dotGroupProps={{ redDot: true }}
                />

                <SHStack paddingLeft={2}>
                  <Controller
                    render={({ field: { ref, ...others } }) => {
                      return (
                        <SHFormControlLabel
                          {...others}
                          label={"Manager details"}
                          size={"small"}
                          control={
                            <SHCheckbox
                              size={"small"}
                              checked={formMethod.getValues("managerDownload")}
                            />
                          }
                        />
                      );
                    }}
                    name={"managerDownload"}
                  />

                  <Controller
                    render={({ field: { ref, ...others } }) => {
                      return (
                        <SHFormControlLabel
                          {...others}
                          label={"Series details"}
                          size={"small"}
                          control={
                            <SHCheckbox
                              size={"small"}
                              checked={formMethod.getValues("seriesDownload")}
                            />
                          }
                        />
                      );
                    }}
                    name={"seriesDownload"}
                  />

                  <Controller
                    render={({ field: { ref, ...others } }) => {
                      return (
                        <SHFormControlLabel
                          {...others}
                          label={"Model details"}
                          size={"small"}
                          control={
                            <SHCheckbox
                              size={"small"}
                              checked={formMethod.getValues("modelDownload")}
                            />
                          }
                        />
                      );
                    }}
                    name={"modelDownload"}
                  />
                </SHStack>
              </SHStack>
            </SHStack>
          </SHStack>

          <SHStack
            direction={"row"}
            marginTop={3}
            padding={4}
            sx={{
              backgroundColor: palette.secondary["50"],
              borderRadius: "12px",
              width: "100%",
            }}
          >
            <SHStack
              direction="row"
              gap={2}
              width={"50%"}
              alignItems={"center"}
              justifyContent={"start"}
            >
              <SHTypography
                variant="body1"
                colorVariant="third"
                lineHeight={1.2}
              >
                {selectedModelIds?.length} of{" "}
                {MSExcelDownloadConstants.MaxSelected} selected
              </SHTypography>

              <SHButton
                disabled={isExcelButtonDisabled}
                isLoading={isExportingExcel}
                variant="contained"
                size="extraMedium"
                disableRipple
                sx={{
                  width: "fit-content",
                  paddingX: 3,
                  "&:disabled.sh-btn-with-icon": {
                    fill: palette.text.disabled,
                    border: `1px solid ${hexToRGBA(palette.primary.dark, 0.3)}`,
                    backgroundColor: palette.primary[50],
                    color: palette.common.white,
                  },
                }}
                startIcon={
                  <DownloadSVG
                    width={12}
                    height={13}
                    color={palette.text.white}
                  />
                }
                onClick={handleOnExcelDownload}
              >
                MS Excel
              </SHButton>
            </SHStack>

            <SHStack
              direction="row"
              gap={2}
              width={"50%"}
              alignItems={"center"}
              justifyContent={"end"}
            >
              <SHTypography
                variant="body1"
                colorVariant="third"
                lineHeight={1.2}
              >
                {selectedModelIds?.length} of{" "}
                {MSWordDownloadConstants.MaxSelected} selected
              </SHTypography>

              <SHButton
                variant="contained"
                size="extraMedium"
                disabled
                disableRipple
                sx={{
                  width: "fit-content",
                  paddingX: 3,
                  "&:disabled.sh-btn-with-icon": {
                    fill: palette.text.disabled,
                    border: `1px solid ${hexToRGBA(palette.primary.dark, 0.3)}`,
                    backgroundColor: palette.primary[50],
                    color: palette.common.white,
                  },
                }}
                startIcon={
                  <DownloadSVG
                    width={12}
                    height={13}
                    color={palette.text.white}
                  />
                }
                onClick={() => {
                  console.log(formMethod.getValues());
                }}
              >
                <BetaBadge
                  label={"Coming soon"}
                  color={"warning"}
                  sx={{
                    width: "fit-content",
                    height: "12px",
                    "& >.MuiChip-label": {
                      overflow: "unset",
                      paddingTop: "2px",
                      fontSize: "8px",
                    },
                    "&.MuiChip-root": {
                      position: "absolute",
                      top: "-5px",
                      right: "0px",
                    },
                  }}
                />
                MS Word
              </SHButton>
            </SHStack>
          </SHStack>
        </FormProvider>

        <SHStack marginTop={2}>
          <Alert
            severity={"info"}
            sx={{
              borderRadius: "12px !important",
              border: "none !important",
              boxShadow: "none !important",
              backgroundColor: "#E5F6FD",
            }}
            icon={
              <IconButton
                sx={{
                  width: "16px",
                  height: "16px",
                  p: 0,
                  borderStyle: "solid",
                  borderWidth: "1px",
                  backgroundColor: "transparent",
                  borderColor: "#0288D1",
                }}
              >
                <InfoSVG color={"#0288D1"} />
              </IconButton>
            }
          >
            <SHTypography variant={"subtitle2"} textAlign={"left"}>
              Note: You can download up to {MSWordDownloadConstants.MaxSelected}{" "}
              models as MS Word and {MSExcelDownloadConstants.MaxSelected}{" "}
              models as MS Excel
            </SHTypography>
          </Alert>
        </SHStack>
      </DialogContent>
    </SHDialog>
  );
};
